import { motion, AnimatePresence } from "framer-motion";
import { Bot, Cpu, ScanLine, Workflow, Sparkles, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { AIArchitectureCard } from "./AIArchitectureCard";
import { DigitalEmployeeCard } from "./DigitalEmployeeCard";
import { RPAAutomationCard } from "./RPAAutomationCard";
import { OCRDocumentCard } from "./OCRDocumentCard";
import { useLanguage } from "@/contexts/LanguageContext";

export function AITechSection() {
  const { t } = useLanguage();
  const [activeFeature, setActiveFeature] = useState(0);

  const aiFeatures = [
    {
      id: "ai-native",
      icon: Bot,
      title: t("ai.native.title"),
      subtitle: t("ai.native.subtitle"),
      description: t("ai.native.desc"),
      highlights: [t("ai.native.h1"), t("ai.native.h2"), t("ai.native.h3"), t("ai.native.h4")],
      stats: { value: t("ai.native.statValue"), label: t("ai.native.statLabel") },
    },
    {
      id: "rpa",
      icon: Workflow,
      title: t("ai.rpa.title"),
      subtitle: t("ai.rpa.subtitle"),
      description: t("ai.rpa.desc"),
      highlights: [t("ai.rpa.h1"), t("ai.rpa.h2"), t("ai.rpa.h3"), t("ai.rpa.h4")],
      stats: { value: t("ai.rpa.statValue"), label: t("ai.rpa.statLabel") },
    },
    {
      id: "ocr",
      icon: ScanLine,
      title: t("ai.ocr.title"),
      subtitle: t("ai.ocr.subtitle"),
      description: t("ai.ocr.desc"),
      highlights: [t("ai.ocr.h1"), t("ai.ocr.h2"), t("ai.ocr.h3"), t("ai.ocr.h4")],
      stats: { value: t("ai.ocr.statValue"), label: t("ai.ocr.statLabel") },
    },
  ];

  const currentFeature = aiFeatures[activeFeature]!;

  return (
    <section id="ai-tech" className="py-12 bg-background relative overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-secondary/5 via-transparent to-transparent" />
        <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-accent/5 via-transparent to-transparent" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-ai text-primary-foreground text-sm font-semibold mb-6 shadow-ai"
          >
            <Sparkles className="w-4 h-4" />
            {t("ai.badge")}
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t("ai.title1") && <span className="text-foreground">{t("ai.title1")} </span>}
            <span className="text-gradient-ai">{t("ai.title2")}</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            {t("ai.subtitle")}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start mb-20">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="flex flex-nowrap gap-2 lg:gap-3 scrollbar-none" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {aiFeatures.map((feature, index) => {
                const isActive = activeFeature === index;
                const isPurple = index === 0 && isActive;
                const isBlueCyan = index === 2 && isActive;
                return (
                  <motion.button
                    key={feature.id}
                    onClick={() => setActiveFeature(index)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`px-3 lg:px-4 py-2 lg:py-2.5 rounded-xl font-medium transition-all duration-300 flex items-center gap-1.5 whitespace-nowrap flex-shrink-0 text-xs lg:text-sm ${
                      isActive
                        ? isPurple
                          ? "bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-purple-500/25"
                          : isBlueCyan
                            ? "bg-gradient-to-r from-blue-600 to-violet-500 text-white shadow-lg shadow-violet-500/25"
                            : "bg-gradient-ai text-primary-foreground shadow-ai"
                        : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground border border-border"
                    }`}
                  >
                    <feature.icon className="w-4 h-4" />
                    <span className="hidden sm:inline">{feature.title}</span>
                    <span className="sm:hidden">{feature.title.split(' ')[0]}</span>
                  </motion.button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={activeFeature}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <div>
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg ${
                      activeFeature === 0 
                        ? "bg-gradient-to-br from-violet-500 to-purple-600 shadow-purple-500/25" 
                        : activeFeature === 2
                          ? "bg-gradient-to-br from-blue-600 to-violet-500 shadow-violet-500/25"
                          : "bg-gradient-ai shadow-ai"
                    }`}>
                      <currentFeature.icon className="w-7 h-7 text-primary-foreground" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-foreground">{currentFeature.title}</h3>
                      <p className="text-sm text-muted-foreground">{currentFeature.subtitle}</p>
                    </div>
                  </div>
                  
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {currentFeature.description}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {currentFeature.highlights.map((highlight, idx) => (
                    <motion.div
                      key={highlight}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-center gap-2"
                    >
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 ${
                        activeFeature === 0 ? "text-purple-500" : activeFeature === 2 ? "text-violet-500" : "text-secondary"
                      }`} />
                      <span className="text-foreground">{highlight}</span>
                    </motion.div>
                  ))}
                </div>

                <div className="flex items-center gap-4">
                  <div className={`px-5 py-3 rounded-xl inline-flex items-center gap-3 ${
                    activeFeature === 0 
                      ? "bg-gradient-to-r from-violet-500 to-purple-600" 
                      : activeFeature === 2
                        ? "bg-gradient-to-r from-blue-600 to-violet-500"
                        : "bg-gradient-ai"
                  }`}>
                    <span className="text-2xl font-bold text-primary-foreground">{currentFeature.stats.value}</span>
                    <span className="text-sm text-primary-foreground/80">{currentFeature.stats.label}</span>
                  </div>
                  <Button variant="ghost" className={`${
                    activeFeature === 0 
                      ? "text-purple-500 hover:text-purple-600" 
                      : activeFeature === 2
                        ? "text-violet-500 hover:text-violet-600"
                        : "text-secondary hover:text-secondary"
                  }`}>
                    {t("ai.learnMore")}
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <AnimatePresence mode="wait">
              {activeFeature === 0 && (
                <motion.div key="architecture" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.3 }}>
                  <AIArchitectureCard />
                </motion.div>
              )}
              {activeFeature === 1 && (
                <motion.div key="rpa" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.3 }}>
                  <RPAAutomationCard />
                </motion.div>
              )}
              {activeFeature === 2 && (
                <motion.div key="ocr" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.3 }}>
                  <OCRDocumentCard />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 rounded-2xl bg-muted/50 border border-border">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-secondary" />
              <span className="text-foreground font-medium">{t("ai.cta")}</span>
            </div>
            <Button asChild variant="hero" size="lg">
              <a
                href="https://www.deep-insights.ai/book-demo"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("ai.ctaBtn")}
                <ArrowRight className="w-5 h-5" />
              </a>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
